/**
 * ViewTabs Component
 *
 * Tab switcher for toggling between Beats and References views.
 * Simple two-tab interface with active state highlighting.
 */

import { CSSProperties } from 'react';
import { useViewStore, type ViewType } from '@/stores/viewStore';
import { useProjectStore } from '@/stores/projectStore';

function ViewTabs() {
  const activeView = useViewStore((state) => state.activeView);
  const setActiveView = useViewStore((state) => state.setActiveView);
  const setLastSelectedBeatIds = useViewStore((state) => state.setLastSelectedBeatIds);
  const setLastSelectedReferenceIds = useViewStore((state) => state.setLastSelectedReferenceIds);
  const lastSelectedBeatIds = useViewStore((state) => state.lastSelectedBeatIds);
  const lastSelectedReferenceIds = useViewStore((state) => state.lastSelectedReferenceIds);

  // Project store selection access
  const selectedNodeIds = useProjectStore((state) => state.selectedNodeIds);
  const selectedReferenceIds = useProjectStore((state) => state.selectedReferenceIds);
  const selectNodes = useProjectStore((state) => state.selectNodes);
  const selectReferenceNodes = useProjectStore((state) => state.selectReferenceNodes);

  const handleTabClick = (view: ViewType) => {
    // Save current selection before switching
    if (activeView === 'beats') {
      setLastSelectedBeatIds(selectedNodeIds);
    } else if (activeView === 'references') {
      setLastSelectedReferenceIds(selectedReferenceIds);
    }

    // Switch to new view
    setActiveView(view);

    // Restore previous selection for the new view (use setTimeout to ensure view switch completes first)
    setTimeout(() => {
      if (view === 'beats' && lastSelectedBeatIds.length > 0) {
        selectNodes(lastSelectedBeatIds);
      } else if (view === 'references' && lastSelectedReferenceIds.length > 0) {
        selectReferenceNodes(lastSelectedReferenceIds);
      }
    }, 0);
  };

  // ---------------------------------------------------------------------------
  // STYLES
  // ---------------------------------------------------------------------------

  const containerStyles: CSSProperties = {
    display: 'flex',
    gap: 'var(--nb-space-xs)',
    alignItems: 'center',
  };

  const tabStyles = (isActive: boolean): CSSProperties => ({
    background: 'none',
    border: 'none',
    padding: 'var(--nb-space-xs) var(--nb-space-md)',
    fontSize: 'var(--nb-font-size-sm)',
    fontWeight: isActive ? 600 : 400,
    color: isActive ? 'var(--nb-text-primary)' : 'var(--nb-text-secondary)',
    cursor: 'pointer',
    borderBottom: isActive ? '2px solid var(--nb-primary)' : '2px solid transparent',
    transition: 'all var(--nb-transition-fast)',
    userSelect: 'none',
  });

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <div style={containerStyles}>
      <button
        style={tabStyles(activeView === 'beats')}
        onClick={() => handleTabClick('beats')}
        title="Switch to Beats view"
      >
        Beats
      </button>
      <button
        style={tabStyles(activeView === 'references')}
        onClick={() => handleTabClick('references')}
        title="Switch to References view"
      >
        References
      </button>
    </div>
  );
}

export default ViewTabs;
